import React, { useState, useEffect } from "react";
import { Box, Typography, Tooltip, Stack, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";

const CHECKIN_CATEGORIES = [
  { id: "Upcoming Stay", label: "Upcoming Stay", color: "#BFDBFE" }, // Light Blue
  { id: "Early Checked In", label: "Early Checked In", color: "#86EFAC" }, // Light Green
  { id: "Checked In", label: "Checked In", color: "#A7F3D0" }, // Light Emerald
  { id: "Staying Guest", label: "Staying Guests", color: "#FDE68A" }, // Light Amber
  { id: "Same Day Checked Out", label: "Same Day Checked Out", color: "#FECACA" }, // Light Red
];

const CHECKOUT_CATEGORIES = [
  { id: "Upcoming Checkout", label: "Upcoming Checkout", color: "#FBCFE8" }, // Light Pink
  { id: "Checked Out", label: "Checked Out", color: "#E9D5FF" }, // Light Purple
  { id: "Same Day Checked Out", label: "Same Day Checked Out", color: "#FECACA" }, // Light Red
  { id: "Early Checked Out", label: "Early Checked Out", color: "#CBD5E1" }, // Light Slate
];

// Custom styled tooltip for a popover-like look
const InteractiveTooltip = styled(({ className, segmentcolor, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ segmentcolor }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "#374151",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    border: `1px solid ${segmentcolor || "#e2e8f0"}`,
    borderRadius: "12px",
    padding: 0,
    minWidth: "300px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#ffffff",
    "&::before": {
      border: `1px solid ${segmentcolor || "#e2e8f0"}`,
    },
  },
}));

const SummaryWidget = ({ setSearchTerm }) => {
  const [totalRooms, setTotalRooms] = useState(null);
  const [dailyReservations, setDailyReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleReservationClick = (resId) => {
    if (setSearchTerm) setSearchTerm(String(resId));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("authToken");
        
        // Fetch total rooms
        const roomsResponse = await fetch("/api/fdo/total-rooms", {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          }
        });
        
        if (roomsResponse.ok) {
          const roomsData = await roomsResponse.json();
          if (roomsData.totalRooms) setTotalRooms(roomsData.totalRooms);
        } else {
          const errData = await roomsResponse.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to load total rooms.");
        }

        // Fetch strictly TODAY's reservations for the widget
        const todayStr = new Date().toISOString().split("T")[0];
        const queryParams = new URLSearchParams({
          _: Date.now(),
          startDate: todayStr,
          endDate: todayStr
        });
        
        const resResponse = await fetch(`/api/fdo/reservations?${queryParams.toString()}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (resResponse.ok) {
          const resData = await resResponse.json();
          const records = resData.data || resData.records || [];
          
          const formatted = records.map(r => {
            let stackVal = (r.status || "Unknown").trim();
            const lower = stackVal.toLowerCase().replace(/[-_]/g, " ");
            
            if (lower === "early checked in" || lower === "early check in" || lower === "early checkin") stackVal = "Early Checked In";
            else if (lower === "same day checked out" || lower === "same day check out" || lower === "same day checkout") stackVal = "Same Day Checked Out";
            else if (lower === "early checked out" || lower === "early check out" || lower === "early checkout") stackVal = "Early Checked Out";
            else if (lower === "checked in" || lower === "checkin") stackVal = "Checked In";
            else if (lower === "checked out" || lower === "checkout") stackVal = "Checked Out";
            else if (lower === "upcoming stay") stackVal = "Upcoming Stay";
            else if (lower === "upcoming checkout") stackVal = "Upcoming Checkout";
            else if (lower === "staying guest") stackVal = "Staying Guest";
            else if (lower === "no show" || lower === "noshow") stackVal = "No Show";

            return {
              reservationId: r.reservation_id || r.id,
              listingName: r.listing_name,
              stack: stackVal
            };
          });
          setDailyReservations(formatted);
        }
      } catch (error) {
        console.error("Failed to fetch widget data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const getSegments = (categories) => {
    return categories.map((category) => {
      const matchingReservations = dailyReservations.filter((r) => r.stack === category.id);
      return { ...category, count: matchingReservations.length, items: matchingReservations };
    });
  };

  const checkInSegments = getSegments(CHECKIN_CATEGORIES);
  const checkOutSegments = getSegments(CHECKOUT_CATEGORIES);

  const occupiedCheckIn = checkInSegments.reduce((acc, curr) => acc + curr.count, 0);
  const occupiedCheckOut = checkOutSegments.reduce((acc, curr) => acc + curr.count, 0);

  if (isLoading) {
    return (
      <Grid container spacing={4} sx={{ animation: "pulse 1.5s infinite ease-in-out" }}>
        {[1, 2].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Box sx={{ width: "120px", height: "16px", backgroundColor: "#e5e7eb", borderRadius: "4px" }} />
              <Box sx={{ width: "80px", height: "16px", backgroundColor: "#e5e7eb", borderRadius: "4px" }} />
            </Box>
            <Box sx={{ width: "100%", height: "14px", borderRadius: "6px", backgroundColor: "#e5e7eb" }} />
            <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
              <Box sx={{ width: "80px", height: "12px", backgroundColor: "#e5e7eb", borderRadius: "4px" }} />
              <Box sx={{ width: "80px", height: "12px", backgroundColor: "#e5e7eb", borderRadius: "4px" }} />
              <Box sx={{ width: "80px", height: "12px", backgroundColor: "#e5e7eb", borderRadius: "4px" }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "error.main" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const effectiveTotal = totalRooms || Math.max(30, occupiedCheckIn, occupiedCheckOut);

  const renderBar = (title, segments, totalOccupied, percentageLabel) => {
    const activePercentage = effectiveTotal > 0 ? Math.round((totalOccupied / effectiveTotal) * 100) : 0;

    return (
      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151" }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151" }}>
            {totalOccupied} / {effectiveTotal} Rooms ({activePercentage}% {percentageLabel})
          </Typography>
        </Box>

        {/* Progress Bar Container */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "14px",
            borderRadius: "6px",
            overflow: "hidden",
            backgroundColor: "#e5e7eb",
          }}
        >
          {segments.map((segment) => {
            if (segment.count === 0) return null;
            const percentage = (segment.count / effectiveTotal) * 100;

            const tooltipContent = (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#374151",
                    fontWeight: 700,
                    mb: 1.5,
                    borderBottom: `2px solid ${segment.color}`,
                    pb: 1,
                  }}
                >
                  {segment.label} ({segment.count})
                </Typography>

                <Stack spacing={1.5} sx={{ maxHeight: "300px", overflowY: "auto", pr: 1 }}>
                  {segment.items.map((item) => (
                    <Box
                      key={item.reservationId}
                      onClick={() => handleReservationClick(item.reservationId)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.85rem",
                        cursor: "pointer",
                        p: 0.5,
                        borderRadius: "4px",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: segment.color }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                          {item.listingName || "Unknown"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600 }}>
                        RID: {item.reservationId}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );

            return (
              <InteractiveTooltip
                key={segment.id}
                title={tooltipContent}
                disableInteractive={false}
                arrow
                placement="bottom"
                segmentcolor={segment.color}
              >
                <Box
                  sx={{
                    width: `${percentage}%`,
                    height: "100%",
                    backgroundColor: segment.color,
                    transition: "width 0.3s ease",
                    cursor: "pointer",
                    "&:hover": { filter: "brightness(0.95)" },
                  }}
                />
              </InteractiveTooltip>
            );
          })}
        </Box>

        {/* Legend below the bar */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
          {segments.map((segment) => (
            <Box key={segment.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: segment.color,
                }}
              />
              <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "#4b5563", fontWeight: 500 }}>
                {segment.label} ({segment.count})
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Stack spacing={3}>
      {renderBar("Occupancy / Arrivals", checkInSegments, occupiedCheckIn, "Active")}
      {renderBar("Departures", checkOutSegments, occupiedCheckOut, "Departing")}
    </Stack>
  );
};

export default SummaryWidget;
